import { useCallback, useEffect, useRef, useState } from 'react';
import { strapTemplateHasDesign, type StorylineBlock } from '@airpost/shared';
import { t } from '../i18n.js';
import { WORDS_PER_SECOND, buildTimeline, clipAt, pipAt, readingLight, ridersAt, segmentAt, timelineTotal, type AtFor, type Clip, type Overlay, type Segment, type TimelineCue } from '../storyline-timeline.js';
import { StrapArtView } from '../strap-view.js';
import { mmss } from '../util.js';

/**
 * The storyline as it goes on air, beside the boxes: a screen that plays the boxes in order (the clips from the
 * playout's proxy copies, the spoken text at reading speed on the autocue, the titles and cards over the pictures
 * for their CG duration), with a timeline to jump around. A VO inside the presenter's ON plays in a window beside the
 * presenter (picture in picture, ADR 091). The box on air is reported back so the list can show it.
 */
interface Props {
  blocks: StorylineBlock[];
  /** The second each box asks to come in inside the box before it (boxes inside boxes). */
  atFor?: AtFor;
  /** The storyboard cue under each box (by box index), for the clip and the CG duration. */
  cues: Array<TimelineCue | null>;
  onBox?: (index: number | null) => void;
}

const TICK_MS = 100;

export function StorylinePlayer({ blocks, cues, atFor, onBox }: Props) {
  const timeline = buildTimeline(blocks, (i) => cues[i] ?? null, atFor);
  const total = timelineTotal(timeline);
  const [t0, setT0] = useState(0);
  const tRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [noPreview, setNoPreview] = useState<string | null>(null);
  const [pipFailed, setPipFailed] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pipRef = useRef<HTMLVideoElement>(null);
  const seek = useCallback((x: number) => {
    tRef.current = x;
    setT0(x);
  }, []);
  const time = Math.min(t0, total);
  const seg = segmentAt(timeline, time);
  const elapsed = seg ? time - seg.start : 0;

  // the clock
  useEffect(() => {
    if (!playing) return;
    let last: number | null = null;
    const id = window.setInterval(() => {
      const now = performance.now();
      const next = tRef.current + (last === null ? TICK_MS / 1000 : (now - last) / 1000);
      last = now;
      if (next >= total) {
        seek(total);
        setPlaying(false);
      } else seek(next);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [playing, total, seek]);
  // the storyline shrank under the cursor
  useEffect(() => {
    if (tRef.current > total) seek(total);
  }, [total, seek]);
  // the box on air, for the list
  const airing = playing && seg ? seg.index : null;
  useEffect(() => onBox?.(airing), [airing, onBox]);

  // the pictures: the clip of the segment (its own, or the one still rolling), kept in step with the clock
  const clip = seg ? clipAt(seg, elapsed) : null;
  useClipVideo(videoRef, clip, playing, time);
  // the window beside the presenter: a VO inside the ON, without its sound
  const pip = seg ? pipAt(seg, elapsed) : null;
  useClipVideo(pipRef, pip, playing, time, true);

  const play = () => {
    if (!timeline.length) return;
    if (tRef.current >= total) seek(0);
    setPlaying(true);
  };
  const pause = () => setPlaying(false);
  const stop = () => {
    setPlaying(false);
    seek(0);
  };
  const prev = () => {
    if (!seg) return;
    const k = timeline.indexOf(seg);
    seek(time - seg.start > 1.5 || k === 0 ? seg.start : timeline[k - 1]!.start);
  };
  const next = () => {
    if (!seg) return;
    const k = timeline.indexOf(seg);
    if (k + 1 < timeline.length) seek(timeline[k + 1]!.start);
    else {
      seek(total);
      setPlaying(false);
    }
  };
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON' && e.key === ' ') return;
    if (e.key === ' ') (playing ? pause : play)();
    else if (e.key === 'ArrowLeft') prev();
    else if (e.key === 'ArrowRight') next();
    else if (e.key === 'Home') stop();
    else return;
    e.preventDefault();
  };

  const showVideo = !!clip && seg?.box.kind !== 'card';
  const failed = !!clip && noPreview === clip.mediaAssetId;
  return (
    <div className="slp" tabIndex={0} onKeyDown={onKey} aria-label={t('slpTitle')}>
      <div className="slp-head">
        <span>{t('slpTitle')}</span>
        <span>{timeline.length ? `${timeline.length} · ${mmss(total)}` : ''}</span>
      </div>
      <div className="slp-screen">
        <video ref={videoRef} className={`slp-video${showVideo && !failed ? '' : ' hidden'}`} playsInline preload="auto" onError={() => clip && setNoPreview(clip.mediaAssetId)} onLoadedData={() => setNoPreview(null)} />
        {(!seg || seg.studio || failed) && (
          <div className="slp-studio">
            <span>{failed ? t('slpNoPreview') : t('slpStudio')}</span>
          </div>
        )}
        {!timeline.length && <div className="slp-placeholder">{t('slpEmpty')}</div>}
        {pip && (
          <div className="slp-pip" title={t('slpPip')}>
            <video ref={pipRef} className={pipFailed === pip.mediaAssetId ? 'hidden' : ''} playsInline preload="auto" muted onError={() => setPipFailed(pip.mediaAssetId)} onLoadedData={() => setPipFailed(null)} />
            <span className="slp-pip-tag">{pipFailed === pip.mediaAssetId ? t('slpNoPreview') : 'VO'}</span>
          </div>
        )}
        {seg?.overlay && <OverlayView overlay={seg.overlay} elapsed={elapsed} playing={playing} aside={!!pip} />}
        {seg && ridersAt(seg, elapsed).map((r) => (r.overlay ? <OverlayView key={r.index} overlay={r.overlay} elapsed={elapsed - r.atS} playing={playing} /> : null))}
        <div className="slp-top">
          <span className={`slp-onair${playing ? ' live' : ''}`}>● {playing ? t('slpOnAir') : t('slpPreview')}</span>
          <span className="slp-label">{seg ? `${seg.index + 1} · ${seg.label} · ${mmss(Math.floor(elapsed))} / ${mmss(seg.duration)}` : ''}</span>
        </div>
      </div>
      <div className="slp-controls">
        <button className="btn small" type="button" onClick={prev} disabled={!seg} title={t('slpPrev')}>
          ⏮
        </button>
        {playing ? (
          <button className="btn small primary" type="button" onClick={pause} title={t('slpPause')}>
            ⏸
          </button>
        ) : (
          <button className="btn small primary" type="button" onClick={play} disabled={!timeline.length} title={t('slpPlay')}>
            ▶
          </button>
        )}
        <button className="btn small" type="button" onClick={stop} disabled={!seg || (time === 0 && !playing)} title={t('slpStop')}>
          ⏹
        </button>
        <button className="btn small" type="button" onClick={next} disabled={!seg} title={t('slpNext')}>
          ⏭
        </button>
        <span className="slp-time">
          {mmss(Math.floor(time))} / {mmss(total)}
        </span>
      </div>
      {timeline.length > 0 && (
        <div className="slp-bar" role="list" aria-label={t('slpTitle')}>
          {timeline.map((s) => (
            <SegmentButton key={s.index} seg={s} now={s === seg} time={time} onSeek={seek} />
          ))}
        </div>
      )}
      <p className="slp-hint">{t('slpHint')}</p>
    </div>
  );
}

function SegmentButton({ seg, now, time, onSeek }: { seg: Segment; now: boolean; time: number; onSeek: (t: number) => void }) {
  const pct = now ? Math.min(100, ((time - seg.start) / seg.duration) * 100) : time >= seg.start + seg.duration ? 100 : 0;
  const vo = seg.box.kind === 'video' && !seg.box.sound ? ' vo' : '';
  return (
    <button type="button" role="listitem" className={`seg ${seg.box.kind}${vo}${now ? ' now' : ''}`} style={{ flexGrow: seg.duration }} title={`${seg.index + 1} · ${seg.label} · ${mmss(seg.duration)}${seg.riders.map((r) => ` · ${r.label} ${mmss(r.atS)}`).join('')}`} onClick={() => onSeek(seg.start)}>
      <i className="fill" style={{ width: `${pct}%` }} />
      {seg.riders.map((r) => (
        <i key={r.index} className="mark" style={{ left: `${(r.atS / seg.duration) * 100}%` }} />
      ))}
    </button>
  );
}

/** Keeps a <video> on a clip, in step with the clock: its file, its second, its sound (or none), playing or paused. */
function useClipVideo(ref: React.RefObject<HTMLVideoElement | null>, clip: Clip | null, playing: boolean, time: number, silent = false) {
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (!clip) {
      if (!v.paused) v.pause();
      return;
    }
    const src = clip.editId ? `/api/edits/${encodeURIComponent(clip.editId)}/output${clip.editAt ? `?v=${encodeURIComponent(clip.editAt)}` : ''}` : `/api/media/${encodeURIComponent(clip.mediaAssetId)}/proxy`;
    if (v.dataset.src !== src) {
      v.dataset.src = src;
      v.src = src;
    }
    const want = clip.inS + (time - clip.rolledAt);
    if (Math.abs(v.currentTime - want) > 0.6 && Number.isFinite(want)) v.currentTime = want;
    v.muted = silent || !clip.sound;
    if (playing) {
      if (v.paused) void v.play().catch(() => undefined);
    } else if (!v.paused) v.pause();
  }, [ref, clip, playing, time, silent]);
}

function OverlayView({ overlay, elapsed, playing, aside }: { overlay: Overlay; elapsed: number; playing: boolean; aside?: boolean }) {
  switch (overlay.kind) {
    case 'prompter':
      return <Prompter text={overlay.text} mode={overlay.mode} elapsed={elapsed} playing={playing} aside={aside} />;
    case 'cg':
      if (strapTemplateHasDesign(overlay.template)) return <StrapArtView lines={overlay.lines} />;
      return (
        <div className={`cg-preview ${overlay.template}`} aria-hidden="true">
          <div className="strap">
            {overlay.lines.length === 0 && <div className="l1">…</div>}
            {overlay.lines.map((l, i) => (
              <div key={i} className={i === 0 ? 'l1' : 'l2'}>
                {l}
              </div>
            ))}
          </div>
        </div>
      );
    case 'badge':
      return <div className="slp-badge">🔊 {overlay.text}</div>;
    case 'placeholder':
      return (
        <div className="slp-placeholder">
          <span>🎬 {t('slpNoClip')}</span>
          {overlay.text && <small>{overlay.text}</small>}
        </div>
      );
  }
}

/**
 * The player's clock ticks ten times a second; between ticks the reading runs on with the screen's frames, so the
 * prompter's light glides. It never runs more than a little past the last tick, and stands still while paused.
 */
function useReading(elapsed: number, playing: boolean): number {
  const [run, setRun] = useState({ from: elapsed, at: 0, now: 0 });
  useEffect(() => {
    if (!playing) return;
    const at = performance.now();
    let id = requestAnimationFrame(function frame(now) {
      setRun({ from: elapsed, at, now });
      id = requestAnimationFrame(frame);
    });
    return () => cancelAnimationFrame(id);
  }, [elapsed, playing]);
  if (!playing || run.from !== elapsed) return elapsed;
  return elapsed + Math.min(0.15, Math.max(0, (run.now - run.at) / 1000));
}

/** The autocue: a soft yellow light moves through the words as they are read and cools to white behind; the text glides to keep the reading in view; `aside` leaves the right of the screen to the window. */
function Prompter({ text, mode, elapsed, playing, aside }: { text: string; mode: 'on' | 'vo'; elapsed: number; playing: boolean; aside?: boolean }) {
  const at = Math.max(0, useReading(elapsed, playing)) * WORDS_PER_SECOND;
  const reading = Math.floor(at) + 1;
  const ref = useRef<HTMLDivElement>(null);
  const nowRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const box = ref.current;
    const el = nowRef.current;
    if (!box || !el) return;
    const want = Math.max(0, el.offsetTop - box.clientHeight * (mode === 'on' ? 0.42 : 0.3));
    if (Math.abs(box.scrollTop - want) > 4) box.scrollTo({ top: want, behavior: 'smooth' });
  }, [reading, mode]);
  let n = 0;
  return (
    <div ref={ref} className={`slp-prompter ${mode}${aside ? ' aside' : ''}`}>
      {text.split(/(\s+)/).map((w, i) => {
        if (!w.trim()) return w;
        n++;
        const [g, b, a] = readingLight(at - (n - 1));
        return (
          <span key={i} ref={n === reading ? nowRef : undefined} style={{ color: `rgba(255, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})` }}>
            {w}
          </span>
        );
      })}
    </div>
  );
}
